import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFile from "components/Input/UploadFile";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import { formatAudCompact } from "../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import { syncCadPayments } from "./accountsApi";
import {
  buildModulePaymentSpecificTask,
  buildPaymentNoteText,
  enrichPaymentsFromNotes,
  getPaymentDeleteConfirmMessage,
  toDisplayPayment,
} from "./modulePaymentNoteUtils";
import { loadModulePaymentsForCad } from "./modulePaymentsRowUtils";
import { getModulePaymentTaskType } from "./modulePaymentTaskTypes";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import TaskModal from "views/admin/Tasks/TaskModal";

const createEmptyDraft = () => ({
  amount: "",
  date: format(new Date(), "yyyy-MM-dd"),
  reference: "",
  notes: "",
});

const sumPayments = (payments) =>
  (payments || []).reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

const PaymentItem = ({ payment, onRemove, onCreateTask, readOnly = false }) => {
  const attachments = payment.attachments || [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {formatAudCompact(Number(payment.amount))}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              · {payment.date || "—"}
            </Typography>
            {payment.reference && (
              <Chip label={payment.reference} size="small" variant="outlined" color="secondary" />
            )}
          </Box>
          {payment.noteText && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: attachments.length ? 0.5 : 0 }}
            >
              {payment.noteText}
            </Typography>
          )}
          {attachments.length > 0 && <PaymentReceiptChips urls={attachments} sx={{}} />}
        </Box>
        {!readOnly && (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Remove payment">
              <IconButton size="small" color="error" onClick={() => onRemove(payment.id)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default function GenericPaymentsModal({
  open,
  onClose,
  moduleName = "",
  cadId = null,
  agreedAmount = 0,
  /** The row's discount, so Due reads the same here as on the row that opened this. */
  discountAmount = 0,
  payments = [],
  onChangePayments,
  onPaymentsSynced,
  onAppendRowNote,
  onRemoveRowNote,
  rowNotes = [],
  clientId = null,
  clientName = "",
  /**
   * Shows the payments this row already carries without offering to write another. A direct
   * payment recorded here skips account verification, so a row that cannot reach the verification
   * flow at all still gets to show its history rather than a way to add to it.
   */
  readOnly = false,
  readOnlyReason = "",
}) {
  const [draft, setDraft] = useState(createEmptyDraft);
  const [uploadData, setUploadData] = useState({ attachments: [] });
  const [taskModal, setTaskModal] = useState({ open: false, payment: null });
  const [isSyncing, setIsSyncing] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [workingPayments, setWorkingPayments] = useState([]);
  const dirtyRef = useRef(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    paymentId: null,
    loading: false,
  });

  const pushPayments = (next) => {
    dirtyRef.current = true;
    setWorkingPayments(next);
    onChangePayments?.(next);
  };

  useEffect(() => {
    if (!open) return undefined;

    dirtyRef.current = false;
    setDraft(createEmptyDraft());
    setUploadData({ attachments: [] });

    let cancelled = false;
    const load = async () => {
      if (!cadId) {
        const seeded = enrichPaymentsFromNotes(payments, rowNotes);
        setWorkingPayments(seeded);
        onChangePayments?.(seeded);
        return;
      }
      setLoadingPayments(true);
      try {
        const loaded = await loadModulePaymentsForCad(cadId, rowNotes);
        if (cancelled) return;
        setWorkingPayments(loaded);
        onChangePayments?.(loaded);
      } catch {
        if (!cancelled) {
          const fallback = enrichPaymentsFromNotes(payments, rowNotes);
          setWorkingPayments(fallback);
          onChangePayments?.(fallback);
        }
      } finally {
        if (!cancelled) setLoadingPayments(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, cadId]);

  const displayPayments = useMemo(
    () => workingPayments.map((p) => toDisplayPayment(p, rowNotes)),
    [workingPayments, rowNotes]
  );

  const syncToBackend = async (list) => {
    if (!cadId) return null;
    const payload = (list || []).map((p) => ({
      uuid: p.id,
      amount: p.amount,
      date: p.date,
      reference: p.reference,
      createdAt: p.createdAt,
    }));
    return syncCadPayments(cadId, payload);
  };

  const handleClose = async () => {
    if (!cadId) {
      toast.warning("Save the entry first to enable payment tracking.");
      onClose();
      return;
    }
    if (!dirtyRef.current) {
      onClose();
      return;
    }
    setIsSyncing(true);
    try {
      const synced = await syncToBackend(workingPayments);
      onPaymentsSynced?.(synced);
      dirtyRef.current = false;
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to save payments."
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAdd = async () => {
    const amt = Number(draft.amount);
    if (!amt || amt <= 0) {
      toast.warning("Enter a valid payment amount.");
      return;
    }
    if (!draft.date) {
      toast.warning("Enter a payment date.");
      return;
    }

    const attachments = [...(uploadData.attachments || [])];
    const noteUuid = crypto.randomUUID();
    const paymentId = crypto.randomUUID();

    const noteText = buildPaymentNoteText({
      amount: amt,
      date: draft.date,
      reference: draft.reference,
      notes: draft.notes,
      attachments,
    });

    const entry = {
      id: paymentId,
      amount: String(amt),
      date: draft.date,
      reference: draft.reference.trim(),
      createdAt: new Date().toISOString(),
      noteUuid,
      attachments,
      noteText: draft.notes?.trim() || null,
    };

    // The note is a record OF the payment, not the payment itself: whoever may record a payment may
    // not hold accounts:note:create. Await it so its failure lands before the outcome is announced,
    // and keep the payment either way rather than raising a success and a denial side by side.
    let noteSaved = true;
    if (noteText.trim() && onAppendRowNote) {
      try {
        await onAppendRowNote({ uuid: noteUuid, text: noteText, createdDate: new Date() });
      } catch {
        noteSaved = false;
      }
    }

    pushPayments([...workingPayments, entry]);
    setDraft(createEmptyDraft());
    setUploadData({ attachments: [] });
    if (noteSaved) {
      toast.success("Payment added.");
    } else {
      toast.warning("Payment added, but its note could not be saved.");
    }
  };

  const requestRemovePayment = (paymentId) => {
    setDeleteConfirm({ open: true, paymentId, loading: false });
  };

  const cancelRemovePayment = () => {
    setDeleteConfirm({ open: false, paymentId: null, loading: false });
  };

  const performRemovePayment = async () => {
    const paymentId = deleteConfirm.paymentId;
    if (!paymentId) return;

    const payment = workingPayments.find((p) => p.id === paymentId);
    setDeleteConfirm((prev) => ({ ...prev, loading: true }));

    try {
      if (payment && onRemoveRowNote) {
        await onRemoveRowNote(payment);
      }
      pushPayments(workingPayments.filter((p) => p.id !== paymentId));
      cancelRemovePayment();
    } catch {
      setDeleteConfirm((prev) => ({ ...prev, loading: false }));
    }
  };

  const openTaskModal = (payment) => {
    setTaskModal({ open: true, payment });
  };

  const closeTaskModal = () => {
    setTaskModal({ open: false, payment: null });
  };

  const totalPaid = sumPayments(workingPayments);
  const due = Math.max(0, agreedAmount - totalPaid - (Number(discountAmount) || 0));

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>Payments — {moduleName}</span>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "background.default" }}>
          {loadingPayments ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.paper" }}>
                <Stack direction="row" flexWrap="wrap" gap={2} useFlexGap>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Agreed
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatAudCompact(agreedAmount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Paid
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatAudCompact(totalPaid)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Due
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color={due > 0 ? "warning.main" : "success.main"}
                    >
                      {formatAudCompact(due)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {displayPayments.length > 0 && (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {displayPayments.map((p) => (
                    <PaymentItem
                      key={p.id}
                      payment={p}
                      onRemove={requestRemovePayment}
                      onCreateTask={openTaskModal}
                      readOnly={readOnly}
                    />
                  ))}
                </Stack>
              )}

              {readOnly ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {readOnlyReason ||
                    "This row cannot take a new payment here. Payments go through account verification."}
                </Typography>
              ) : (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Add Payment
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1.5 }}
                    >
                      Notes and attachments are saved once to the row notes (Action column) and
                      shown here too.
                    </Typography>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          label="Amount *"
                          type="number"
                          size="small"
                          value={draft.amount}
                          onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                        <TextField
                          label="Date *"
                          type="date"
                          size="small"
                          value={draft.date}
                          onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                          sx={{ flex: 1 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                      <TextField
                        label="Reference / Receipt #"
                        size="small"
                        value={draft.reference}
                        onChange={(e) => setDraft((d) => ({ ...d, reference: e.target.value }))}
                      />
                      <TextField
                        label="Notes"
                        size="small"
                        multiline
                        rows={2}
                        value={draft.notes}
                        onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                      />
                      <UploadFile
                        data={uploadData}
                        setData={setUploadData}
                        imageKey="attachments"
                        label="Attachments"
                        type="multiple"
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Paper>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isSyncing || loadingPayments}>
            {isSyncing ? "Saving…" : "Done"}
          </Button>
        </DialogActions>
      </Dialog>

      {taskModal.open && taskModal.payment && (
        <TaskModal
          open={taskModal.open}
          handleClose={closeTaskModal}
          module={moduleName}
          title={`Verify payment — ${moduleName}`}
          modalData={{
            name: clientName,
            clientId,
            specificTask: buildModulePaymentSpecificTask(moduleName, taskModal.payment.amount),
          }}
          taskType={getModulePaymentTaskType(moduleName)}
          paymentUuid={taskModal.payment.id}
        />
      )}

      <DeleteConfirmDialog
        open={deleteConfirm.open}
        title="Remove payment?"
        message={getPaymentDeleteConfirmMessage(
          workingPayments.find((p) => p.id === deleteConfirm.paymentId) || {}
        )}
        confirmLabel="Remove payment"
        onConfirm={performRemovePayment}
        onCancel={cancelRemovePayment}
        loading={deleteConfirm.loading}
      />
    </>
  );
}
